import React from 'react'
import { Button, Card, CardContent, Label, Radio } from '@/shared/ui'
import SaveFeedbackModal from '@/shared/ui/SaveFeedbackModal'
import { useToast } from '@/shared/ui/Toast'
import { getCandlesEngine, setCandlesEngine, getDefaultMarketCategory, setDefaultMarketCategory, type CandlesEngine } from '../services/candlesSettingsService'

export default function CandlesEngineSettings() {
  const { add: toast } = useToast()
  const [engine, setEngine] = React.useState<CandlesEngine>('ws_v2')
  const [market, setMarket] = React.useState<'linear'>('linear')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const [e, m] = await Promise.all([
          getCandlesEngine().catch(()=> 'ws_v2' as CandlesEngine),
          getDefaultMarketCategory().catch(()=> 'linear' as const),
        ])
        if (cancelled) return
        setEngine(e)
        // Requisito atual: travar em 'linear' (Perp USDT)
        // Mesmo que o backend retorne outro valor, mantemos 'linear' para estabilidade
        setMarket('linear')
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Falha ao carregar configuração de coleta')
      } finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      await Promise.all([
        setCandlesEngine(engine),
        setDefaultMarketCategory('linear'), // requisito: somente Perp USDT
      ])
      toast({ title: 'Coleta de Candles', description: 'Configurações salvas.', variant: 'success', soft: true })
      setSaved(true)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao salvar configuração')
    } finally { setSaving(false) }
  }

  return (
    <Card>
      <CardContent>
        <form className="space-y-4 pt-4" onSubmit={onSave} aria-busy={saving || loading}>
          {error && (<div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>)}

          <div className="space-y-2">
            <Label>Engine de coleta</Label>
            <div className="flex flex-col gap-2">
              <Radio name="candles_engine" checked={engine==='ws_v2'} onChange={()=> setEngine('ws_v2')} label="v2 • WebSocket (recomendado)" />
              <Radio name="candles_engine" checked={engine==='rest_v1'} onChange={()=> setEngine('rest_v1')} label="v1 • REST (contingência)" />
            </div>
            <div className="text-xs text-foreground/70">A v2 mantém assinaturas em tempo real e reduz filas/gaps. A v1 permanece para contingência e backfill eventual.</div>
          </div>

          <div className="space-y-1.5">
            <Label>Mercado padrão</Label>
            <div className="rounded-md border border-border p-3 text-sm">
              Perp USDT (linear)
            </div>
            <div className="text-xs text-foreground/70">Requisito atual: focar exclusivamente em contratos perp USDT. Ativos sem perp USDT serão desativados.</div>
          </div>

          <Button type="submit" disabled={saving || loading}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </form>
      </CardContent>
      <SaveFeedbackModal open={saved} onClose={()=> setSaved(false)} title="Coleta de Candles" description="Configurações salvas com sucesso." />
    </Card>
  )
}
